<script setup>
import useNavbar from "@composables/base/useNavbar";
import UserMenu from "@components/navbar/UserMenu.vue";
import { useSettingsStore } from "@stores/settings";
import { useMenuStore } from "@stores/menu";
import { useLegitimuzStatusStore } from "@stores/legitimuz-status";
import { useLegitimuzVerification } from "@/composables/base/useLegitimuzVerification";
import { storeToRefs } from 'pinia';

const router = useRouter();
const route = useRoute();

const {
  authStore,
  depositStore,
  promotionStore,
} = useNavbar();

const settingsStore = useSettingsStore();
const menuStore = useMenuStore();
const legitimuzStore = useLegitimuzStatusStore();
const { userStatus } = storeToRefs(legitimuzStore);

const {
  initFaceIndex,
  triggerFaceIndexVerification,
  triggerVerification,
  legitimuzCpf
} = useLegitimuzVerification();

const handleVerification = async (type) => {
  console.log(`Navbar handling verification type: ${type}`);
  try {
    if (type === 'face') {
      // Consider if initFaceIndex needs to be awaited *every* time
      // or only the first time. The composable might handle this.
      await initFaceIndex();
      triggerFaceIndexVerification();
    } else { // Assuming 'kyc'
      // Same consideration for initLegitimuz / triggerVerification
      triggerVerification();
    }
  } catch (error) {
    console.error('Error during verification process:', error);
  }
};

// ---> Initial status check on mount <---
const checkStatus = async () => {
  try {
    if (authStore.isLoggedIn) {
      console.log('Checking legitimuz status on mount (Navbar)');
      await legitimuzStore.checkStatus(true); // Force check
    }
  } catch (error) {
    console.error('Error checking legitimuz status on mount:', error);
  }
};

onMounted(() => {
  checkStatus();
});
</script>

<template>
  <div class="flex w-full select-none items-center bg-brand-navbar min-h-16">
    <div class="items-center h-full bg-brand-navbar-dark border-brand-separator"
      :class="{ 'hidden lg:flex': !menuStore.menuOpened, 'flex': menuStore.menuOpened }">
      <div class="flex items-center gap-4 w-[254px] ">
        <NuxtLink @click="menuStore.modalClose()" active-class="text-white" to="/"
          class="group w-full relative flex cursor-pointer flex-row items-center justify-center gap-x-1 text-xs font-semibold uppercase text-bzMenuText transition-all hover:text-white px-4 py-2">
          <div class="flex pl-2 items-center">
            <Icon name="cil:casino" />
            <span class="ml-1 font-sans">Cassino</span>
          </div>
          <div :class="[route.name === 'index' ? 'h-0.5 w-32' : 'h-0']"
            class="absolute -bottom-4 left-0 right-0 bg-brand-primary transition-all group-hover:h-0.5 w-full"></div>
        </NuxtLink>

        <NuxtLink v-if="settingsStore.settings.cometa_sports_book" @click="menuStore.modalClose()"
          active-class="text-white" to="/sports"
          class="group w-full relative flex cursor-pointer flex-row items-center justify-center gap-x-1 text-xs font-semibold uppercase text-bzMenuText transition-all hover:text-white px-4 py-2">
          <div class="flex pl-2 items-center">
            <Icon name="solar:football-bold" />
            <span class="ml-1 font-sans">Esportes</span>
          </div>
          <div :class="[route.name === 'sports-page' ? 'h-0.5 w-32' : 'h-0']"
            class="absolute -bottom-4 left-0 right-0 bg-brand-primary transition-all group-hover:h-0.5 w-full"></div>
        </NuxtLink>
      </div>
    </div>

    <div class="flex justify-center w-full">
      <BaseContainer class="!py-0">
        <div class="flex w-full flex-row text-white xl:justify-between">
          <div class="flex flex-1 flex-row items-center justify-between gap-3">
            <div class="flex gap-3">
              <div class="flex h-7 max-h-8 w-full">
                <BaseLogo icon />
              </div>
              <NuxtLink
                to="/promotions"
                class="animate-ease-out flex animate-bounce items-center justify-center shadow-brand-primary drop-shadow-lg filter transition-opacity"
                v-if="router.name !== 'promotions' && Array.isArray(promotionStore.state.promotions) && promotionStore.state.promotions.length > 0"
              >
                <div class="animate-wiggle animate-infinite animate-ease-linear">
                  <svg class="h-5 fill-brand-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path
                      d="M190.5 68.8L225.3 128H224 152c-22.1 0-40-17.9-40-40s17.9-40 40-40h2.2c14.9 0 28.8 7.9 36.3 20.8zM64 88c0 14.4 3.5 28 9.6 40H32c-17.7 0-32 14.3-32 32v64c0 17.7 14.3 32 32 32H480c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H438.4c6.1-12 9.6-25.6 9.6-40c0-48.6-39.4-88-88-88h-2.2c-31.9 0-61.5 16.9-77.7 44.4L256 85.5l-24.1-41C215.7 16.9 186.1 0 154.2 0H152C103.4 0 64 39.4 64 88zm336 0c0 22.1-17.9 40-40 40H288h-1.3l34.8-59.2C329.1 55.9 342.9 48 357.8 48H360c22.1 0 40 17.9 40 40zM32 288V464c0 26.5 21.5 48 48 48H224V288H32zM288 512H432c26.5 0 48-21.5 48-48V288H288V512z"
                    />
                  </svg>
                </div>
              </NuxtLink>
            </div>

            <div class="flex items-center gap-2" v-if="!authStore.isLoggedIn">
              <BaseButton class="md:hidden" size="xs" @click="authStore.modalOpen('login')">
                Entrar
              </BaseButton>
              <BaseButton size="xs" @click="authStore.modalOpen('register')"
                class="bg-brand-primary hover:bg-brighten-brand-primary md:hidden">
                Cadastre-se
              </BaseButton>

              <BaseButton class="hidden md:flex" size="sm" @click="authStore.modalOpen('login')">
                Entrar
              </BaseButton>
              <BaseButton size="sm" @click="authStore.modalOpen('register')"
                class="hidden bg-brand-primary hover:bg-brighten-brand-primary md:flex">
                Cadastre-se
              </BaseButton>
            </div>

            <div v-if="authStore.isLoggedIn" class="flex items-center gap-1 sm:gap-2">

              <NavbarVerificationButton
                  :user-status="userStatus"
                  @verify="handleVerification" />

              <NavbarBalance />
              <BaseButton size="xs" variant="primary" class="md:hidden"
                v-if="settingsStore.settings.deposit_enable_popup" @click="depositStore.toggleModal()">
                Depositar
              </BaseButton>
              <BaseButton size="sm" class="hidden md:flex" variant="primary"
                v-if="settingsStore.settings.deposit_enable_popup" @click="depositStore.toggleModal()">
                Depositar
              </BaseButton>

              <NuxtLink v-else href="/user/deposit">
                <div class="rounded-lg border border-gray-700 bg-brand-primary p-2 text-base text-white">
                  Depositar
                </div>
              </NuxtLink>

              <client-only>
                <UserMenu />
              </client-only>
            </div>
          </div>
        </div>
      </BaseContainer>
    </div>
    <LegitimuzElements />
  </div>

  
</template>

<style scoped>
@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>